// StudentExamResultList.jsx (UPDATED)
// ✅ DONE:
// 1) Semester/Year dropdown REMOVED
// 2) Pattern dropdown shows: "PatternName - YEAR 1" / "PatternName - SEMESTER 1" / "PatternName - MONTH 1"
// 3) semNo is taken from selected Pattern (Batch Exam Master.sem_no)
// 4) API returns Data1 => UI reads Data1/data1 properly
// 5) Better backend error display (shows Message/message)
// 6) Route uses api/ExamMaster/GetStudentsWithResultSummary

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Row,
  FormGroup,
  Label,
  Table,
  FormText,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';

import VendorBranchSelector from '../../components/VendorBranchSelector';
import { apiService } from '../../constants/ApiService';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

// ---------- helpers ----------
const pick = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== '');

const getCaseInsensitive = (obj, key) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const foundKey = Object.keys(obj).find(
    (k) => k.toLowerCase() === String(key).toLowerCase(),
  );
  return foundKey ? obj[foundKey] : undefined;
};

// ✅ reads Data / Data1 / data / data1 / List etc.
const normalizeArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const directKeys = [
    'Data',
    'data',
    'Data1',
    'data1',
    'List',
    'list',
    'Result',
    'result',
    'Items',
    'items',
  ];

  for (const k of directKeys) {
    const v = getCaseInsensitive(payload, k);
    if (Array.isArray(v)) return v;
  }

  // if nested like { data: { Data1: [] } }
  const nestedCandidates = [
    getCaseInsensitive(payload, 'data'),
    getCaseInsensitive(payload, 'Data'),
    getCaseInsensitive(payload, 'data1'),
    getCaseInsensitive(payload, 'Data1'),
  ];

  for (const n of nestedCandidates) {
    if (n && typeof n === 'object') {
      const a = normalizeArray(n);
      if (a.length) return a;
    }
  }

  return [];
};

// Course structure helpers
const normalizeCourseStructure = (raw) => {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!s) return 'semester';
  if (s.includes('year')) return 'year';
  if (s.includes('month')) return 'month';
  return 'semester';
};

const structureWordUpper = (raw) => {
  const s = normalizeCourseStructure(raw);
  if (s === 'year') return 'YEAR';
  if (s === 'month') return 'MONTH';
  return 'SEMESTER';
};

// pickers
const getClassId = (x) => Number(x?.id ?? x?.class_id ?? x?.ClassId ?? 0) || 0;

const getClassName = (x) =>
  String(
    pick(
      x?.class_Name,
      x?.class_name,
      x?.class_display_name,
      x?.className,
      x?.course_name,
      '',
    ),
  );

const pickTotalSemester = (x) =>
  Number(pick(x?.total_semester, x?.Total_Semester, x?.totalSemester, 0)) || 0;

const pickCourseStructure = (x) =>
  normalizeCourseStructure(
    pick(
      x?.course_structure,
      x?.Course_Structure,
      x?.courseStructure,
      'semester',
    ),
  );

const getBatchId = (b) =>
  Number(b?.id ?? b?.batch_id ?? b?.ID ?? b?.BatchId ?? 0) || 0;

const getBatchName = (b) =>
  String(pick(b?.batch_name, b?.BatchName, b?.name, b?.Name, ''));

const getBatchCode = (b) =>
  String(pick(b?.batch_code, b?.BatchCode, b?.code, b?.Code, ''));

// react-select styles
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 36, height: 36, fontSize: 14 }),
  valueContainer: (base) => ({ ...base, height: 36, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 36 }),
};

const DETAIL_ROUTE = '/student-exam-result-entry';

const StudentExamResultList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((s) => s.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const [form, setForm] = useState({
    vendor_id: '',
    branch_id: '',

    class_id: '',
    class_name: '',

    course_structure: 'semester',
    total_semester: 0,

    batch_id: '',
    batch_name: '',
    batch_code: '',

    batch_exam_id: '', // ✅ Batch_Exam_Master.id
    pattern_id: '', // CourseExamPatternMaster id
    pattern_name: '',

    // ✅ sem_no now comes ONLY from Pattern selection
    sem_no: '',

    session_year: '',
    search: '',
  });

  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [patternOptions, setPatternOptions] = useState([]);

  const [courseLoading, setCourseLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [patternLoading, setPatternLoading] = useState(false);

  const [rows, setRows] = useState([]);

  // Prefill from navigation (when coming back from entry)
  useEffect(() => {
    const st = location.state;
    if (!st) return;

    setForm((prev) => ({
      ...prev,
      ...st,
      vendor_id: st.vendor_id ?? prev.vendor_id,
      branch_id: st.branch_id ?? prev.branch_id,
      class_id: String(st.class_id ?? st.classId ?? prev.class_id ?? ''),
      class_name: st.class_name ?? st.className ?? prev.class_name,

      course_structure: normalizeCourseStructure(
        st.course_structure ?? prev.course_structure ?? 'semester',
      ),
      total_semester:
        Number(st.total_semester ?? prev.total_semester ?? 0) || 0,

      batch_id: String(st.batch_id ?? st.batchId ?? prev.batch_id ?? ''),
      batch_name: st.batch_name ?? st.batchName ?? prev.batch_name,
      batch_code: st.batch_code ?? st.batchCode ?? prev.batch_code,

      batch_exam_id: String(
        st.batch_exam_id ?? st.batchExamId ?? prev.batch_exam_id ?? '',
      ),
      pattern_id: String(
        st.pattern_id ?? st.patternId ?? prev.pattern_id ?? '',
      ),
      pattern_name: String(st.pattern_name ?? prev.pattern_name ?? ''),

      sem_no: String(st.sem_no ?? st.semNo ?? prev.sem_no ?? ''),

      session_year: st.session_year ?? prev.session_year ?? '',
      search: st.search ?? prev.search ?? '',
    }));
  }, [location.state]);

  // ---------------- Courses ----------------
  const fetchCourses = async (searchText = '') => {
    try {
      const vendorId = form.vendor_id || '';
      const branchId = form.branch_id || '';
      // if (!vendorId || !branchId) return setCourseOptions([]);

      // setCourseLoading(true);
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const list = normalizeArray(res?.data) || normalizeArray(res) || [];
        setCourseOptions(
          (list || [])
            .map((x) => ({
              value: getClassId(x),
              label: getClassName(x) || `Class-${getClassId(x)}`,
              meta: {
                total_semester: pickTotalSemester(x),
                course_structure: pickCourseStructure(x),
              },
            }))
            .filter((o) => o.value > 0),
        );
      } else {
        setCourseOptions([]);
      }
    } catch (e) {
      console.log(e);
      setCourseOptions([]);
    } finally {
      setCourseLoading(false);
    }
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: form.vendor_id,
      branchId: form.branch_id,
    });
    if (!canLoad) return;
    fetchCourses('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendor_id, form.branch_id]);

  // ---------------- Batches ----------------
  const fetchBatches = async (classId, searchText = '') => {
    try {
      const vendorId = form.vendor_id || '';
      const branchId = form.branch_id || '';
      // if (!vendorId || !branchId || !classId) return setBatchOptions([]);

      // setBatchLoading(true);
      const res = await apiService.get(
        `api/BatchMaster/GetAllBatch?vendorId=${vendorId}&branchId=${branchId}&classId=${classId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const list = normalizeArray(res?.data) || normalizeArray(res) || [];
        setBatchOptions(
          (list || [])
            .map((b) => {
              const id = getBatchId(b);
              const name = getBatchName(b);
              const code = getBatchCode(b);
              return {
                value: id,
                label: code ? `${code} - ${name}` : name || `Batch-${id}`,
                meta: { name, code },
              };
            })
            .filter((o) => o.value > 0),
        );
      } else {
        setBatchOptions([]);
      }
    } catch (e) {
      console.log(e);
      setBatchOptions([]);
    } finally {
      setBatchLoading(false);
    }
  };

  // ---------------- Patterns (Batch Exam Master list) ----------------
  const fetchPatterns = async (batchId, searchText = '') => {
    try {
      const vendorId = form.vendor_id || '';
      const branchId = form.branch_id || '';

      // if (!vendorId || !branchId || !batchId) {
      //   setPatternOptions([]);
      //   return;
      // }

      // setPatternLoading(true);

      const res = await apiService.get(
        `api/BatchExamMaster/GetAllBatchExamMaster?vendorId=${vendorId}&branchId=${branchId}&batchId=${batchId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      const api = res?.data?.status !== undefined ? res.data : res;

      if (api?.status !== true) {
        setPatternOptions([]);
        toast.error(api?.Message || api?.message || 'Failed to load patterns');
        return;
      }

      const list =
        api?.Data ||
        api?.data ||
        api?.Data1 ||
        api?.data1 ||
        api?.data?.Data ||
        [];

      const termWord = structureWordUpper(form.course_structure);

      const options = (Array.isArray(list) ? list : []).map((x) => {
        const semNo = Number(x?.sem_no ?? x?.semester_no ?? 0) || 0;
        const pName = (x?.pattern_name || '').trim();

        // ✅ show: "PatternName - YEAR 1" / "PatternName - SEMESTER 2"
        const label =
          semNo > 0
            ? `${pName || `Exam-${x?.id}`} - ${termWord} ${semNo}`
            : pName || `Exam-${x?.id}`;

        return {
          value: Number(x?.id || 0), // ✅ batch_exam_master.id
          label,
          meta: {
            batch_exam_id: Number(x?.id || 0),
            pattern_id: Number(x?.pattern_id || 0),
            pattern_name: pName,
            sem_no: semNo,
          },
        };
      });

      setPatternOptions(options.filter((o) => o.value > 0));
    } catch (err) {
      console.error(err);
      setPatternOptions([]);
      toast.error('Something went wrong while loading patterns');
    } finally {
      setPatternLoading(false);
    }
  };

  useEffect(() => {
    if (!form.class_id) {
      setBatchOptions([]);
      setPatternOptions([]);
      setRows([]);
      setIsLoaded(false);
      return;
    }
    fetchBatches(form.class_id, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.class_id, form.vendor_id, form.branch_id]);

  // ---------------- NEW API: Students with Result Summary ----------------
const fetchStudentsWithSummary = async () => {
  const vendorId = form.vendor_id || '';
  const branchId = form.branch_id || '';
  const classId = form.class_id || '';
  const batchId = form.batch_id || '';
  const sessionYear = form.session_year || '';
  const batchExamId = form.batch_exam_id || '';
  const semNo = Number(form.sem_no || 0) || 0;
  const search = form.search || '';

  if (!vendorId || !branchId || !classId || !batchId || !batchExamId || !semNo) {
    return { ok: false, msg: 'Missing filters', list: [] };
  }

  const url =
    `api/ExamMaster/GetStudentsWithResultSummary` +
    `?vendorId=${vendorId}` +
    `&branchId=${branchId}` +
    `&classId=${classId}` +
    `&batchId=${batchId}` +
    `&sessionYear=${encodeURIComponent(sessionYear)}` +
    `&batchExamId=${batchExamId}` +
    `&semNo=${semNo}` +
    `&search=${encodeURIComponent(search)}`;

  const res = await apiService.get(url);

  if (res?.status !== true) {
    return {
      ok: false,
      msg: res?.Message || res?.message || 'Failed',
      list: [],
    };
  }

  // ✅ IMPORTANT: always pick Data1/data1 directly
  const list = res?.Data1 || res?.data1 || res?.data?.Data1 || res?.data?.data1 || [];

  const mapped = (Array.isArray(list) ? list : [])
    .map((x) => ({
      student_id: Number(
        x?.student_id ?? x?.StudentId ?? x?.studentId ?? 0
      ) || 0,

      roll: String(
        x?.enrollment_number ??
        x?.EnrollmentNumber ??
        x?.roll_no ??
        x?.RollNo ??
        x?.roll ??
        ''
      ),

      student_name: String(
        x?.student_name ?? x?.StudentName ?? x?.name ?? ''
      ),

      // ✅ These must come from API now
      total_marks: x?.total_marks ?? x?.TotalMarks ?? null,
      obtained_marks: x?.obtained_marks ?? x?.ObtainedMarks ?? null,

      grade: String(x?.grade ?? x?.Grade ?? ''),
      remark: String(x?.result_status ?? x?.ResultStatus ?? ''),
    }))
    .filter((r) => r.student_id > 0);

  return {
    ok: true,
    msg: res?.Message || res?.message || 'OK',
    list: mapped,
  };
};


  // ---------------- Handlers ----------------
  const onCourseSelect = (opt) => {
    const classId = opt?.value ? String(opt.value) : '';

    setIsLoaded(false);
    setRows([]);

    setForm((prev) => ({
      ...prev,
      class_id: classId,
      class_name: opt?.label || '',
      course_structure: normalizeCourseStructure(
        opt?.meta?.course_structure || 'semester',
      ),
      total_semester: Number(opt?.meta?.total_semester || 0) || 0,

      batch_id: '',
      batch_name: '',
      batch_code: '',

      batch_exam_id: '',
      pattern_id: '',
      pattern_name: '',

      sem_no: '',
    }));

    if (classId) fetchBatches(classId, '');
  };

  const onBatchSelect = (opt) => {
    const newBatchId = opt?.value ? String(opt.value) : '';

    setIsLoaded(false);
    setRows([]);

    setForm((prev) => ({
      ...prev,
      batch_id: newBatchId,
      batch_name: opt?.meta?.name || '',
      batch_code: opt?.meta?.code || '',

      batch_exam_id: '',
      pattern_id: '',
      pattern_name: '',
      sem_no: '',
    }));

    if (newBatchId) fetchPatterns(newBatchId, '');
    else setPatternOptions([]);
  };

  // ✅ sem_no comes from pattern
  const onPatternSelect = (opt) => {
    setIsLoaded(false);
    setRows([]);

    const beId = Number(opt?.value || 0) || 0; // ✅ batch_exam_master.id
    const semFromPattern = Number(opt?.meta?.sem_no || 0) || 0;

    setForm((prev) => ({
      ...prev,
      batch_exam_id: beId ? String(beId) : '',
      pattern_id: opt?.meta?.pattern_id ? String(opt.meta.pattern_id) : '',
      pattern_name: opt?.meta?.pattern_name || opt?.label || '',
      sem_no: semFromPattern > 0 ? String(semFromPattern) : '',
    }));
  };

  // ---------------- Load ----------------
  const onLoadClick = async () => {
    try {
      setErrors({});

      if (!form.vendor_id || !form.branch_id)
        return toast.error('Please select Vendor/Branch');
      if (!form.class_id) return toast.error('Please select Course/Class');
      if (!form.batch_id) return toast.error('Please select Batch');
      if (!form.batch_exam_id)
        return toast.error('Please select Pattern (Batch Exam)');
      if (!form.sem_no || Number(form.sem_no) <= 0)
        return toast.error(`Semester/Year missing in selected Pattern`);

      setLoading(true);
      setIsLoaded(false);
      setRows([]);

      const { ok, msg, list } = await fetchStudentsWithSummary();

      if (!ok) {
        toast.error(msg || 'Failed to load');
        return;
      }

      if (!list.length) {
        toast.error(msg || 'No students found for selected filters.');
        return;
      }

      setRows(list);
      setIsLoaded(true);
    } catch (e) {
      console.log(e);
      toast.error('Something went wrong while loading students.');
    } finally {
      setLoading(false);
    }
  };

  const onEditStudent = (stu) => {
    navigate(DETAIL_ROUTE, {
      state: {
        vendor_id: form.vendor_id,
        branch_id: form.branch_id,

        class_id: form.class_id,
        class_name: form.class_name,

        course_structure: form.course_structure,
        total_semester: form.total_semester,

        batch_id: form.batch_id,
        batch_name: form.batch_name,
        batch_code: form.batch_code,

        batch_exam_id: form.batch_exam_id,
        pattern_id: form.pattern_id,
        pattern_name: form.pattern_name,

        sem_no: Number(form.sem_no || 0) || null,

        session_year: form.session_year || '',
        search: form.search || '',

        student_id: stu.student_id,
        student_name: stu.student_name,
        roll: stu.roll,
      },
    });
  };

  // ---------------- Selected options ----------------
  const selectedCourseOpt = useMemo(
    () =>
      courseOptions.find((o) => Number(o.value) === Number(form.class_id)) ||
      null,
    [courseOptions, form.class_id],
  );

  const selectedBatchOpt = useMemo(
    () =>
      batchOptions.find((o) => Number(o.value) === Number(form.batch_id)) ||
      null,
    [batchOptions, form.batch_id],
  );

  const selectedPatternOpt = useMemo(
    () =>
      patternOptions.find(
        (o) => Number(o.value) === Number(form.batch_exam_id),
      ) || null,
    [patternOptions, form.batch_exam_id],
  );

  const canLoad =
    !!form.vendor_id &&
    !!form.branch_id &&
    !!form.class_id &&
    !!form.batch_id &&
    !!form.batch_exam_id &&
    !!form.sem_no;

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={form}
        setSelectionData={setForm}
        errorMessage={errors}
        accessButton={true}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='border-bottom p-3 d-flex justify-content-between align-items-center'>
          <span>Student Exam Result (Student List)</span>
          <Button
            color='warning'
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            <i className='bi bi-arrow-left' />
          </Button>
        </CardTitle>

        <CardBody>
          <Row>
            <FormGroup className='col-sm-6'>
              <Label>Course / Class</Label>
              <Select
                styles={selectStyles}
                options={courseOptions}
                value={selectedCourseOpt}
                isLoading={courseLoading}
                isSearchable
                isClearable
                placeholder='Search courses...'
                onChange={onCourseSelect}
                onInputChange={(input, meta) => {
                  if (meta.action === 'input-change') fetchCourses(input);
                }}
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Batch Name</Label>
              <Select
                styles={selectStyles}
                options={batchOptions}
                value={selectedBatchOpt}
                isLoading={batchLoading}
                isSearchable
                isClearable
                placeholder='Search batches...'
                isDisabled={!form.class_id}
                onChange={onBatchSelect}
                onInputChange={(input, meta) => {
                  if (meta.action === 'input-change')
                    fetchBatches(form.class_id, input);
                }}
              />
            </FormGroup>

            <FormGroup className='col-sm-8'>
              <Label>Pattern (from Batch Exam Master)</Label>
              <Select
                styles={selectStyles}
                options={patternOptions}
                value={selectedPatternOpt}
                isLoading={patternLoading}
                isSearchable
                isClearable
                placeholder='Search pattern...'
                isDisabled={!form.batch_id}
                onChange={onPatternSelect}
                onInputChange={(input, meta) => {
                  if (meta.action === 'input-change')
                    fetchPatterns(form.batch_id, input);
                }}
              />
              <FormText color='muted'>
                batch_exam_id: <b>{form.batch_exam_id || '-'}</b> |{' '}
                {structureWordUpper(form.course_structure)}:{' '}
                <b>{form.sem_no || '-'}</b>
              </FormText>
            </FormGroup>

            <FormGroup className='col-sm-4 d-flex align-items-end'>
              <Button
                color='info'
                size='sm'
                style={{ width: '100%' }}
                onClick={onLoadClick}
                disabled={!canLoad || loading}
              >
                {loading ? '...' : 'Load'}
              </Button>
            </FormGroup>
          </Row>

          <hr className='my-4' />

          {!isLoaded ? (
            <div className='text-muted'>
              Select Course/Batch/Pattern and click <b>Load</b>.
            </div>
          ) : (
            <>
              <div className='text-muted mb-2'>
                Students found: <b>{rows.length}</b>
              </div>

              <Table bordered responsive size='sm'>
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th style={{ width: 200 }}>Roll/Enroll</th>
                    <th>Student Name</th>
                    <th>Max Marks</th>
                    <th>Obtained Marks</th>
                    <th style={{ width: 140 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={`stu_${r.student_id}`}>
                      <td>{idx + 1}</td>
                      <td>{r.roll || '-'}</td>
                      <td>{r.student_name || '-'}</td>
                      <td>{r.total_marks || '-'}</td>
                      <td>{r.obtained_marks || '-'}</td>
                      <td>
                        <Button
                          color='primary'
                          size='sm'
                          onClick={() => onEditStudent(r)}
                        >
                          <i className='bi bi-pencil-square' /> Marks
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default StudentExamResultList;
